/**
 * Flash Message Component
 * Created: 2024-03-27
 * Displays flash messages and notifications with animations.
 */

'use client'

import React from 'react'
import {AnimatePresence, motion} from 'framer-motion'
import {FlashMessage as FlashMessageType, useFlashMessage} from '../contexts/flash-message.context'

const variants = {
  initial: {opacity: 0, y: -50},
  animate: {opacity: 1, y: 0},
  exit: {opacity: 0, y: 50}
}

const getBackgroundColor = (type: FlashMessageType['type']) => {
  switch (type) {
    case 'success':
      return 'bg-green-500'
    case 'error':
      return 'bg-red-500'
    case 'warning':
      return 'bg-yellow-500'
    case 'info':
      return 'bg-blue-500'
    default:
      return 'bg-gray-500'
  }
}

const FlashMessageItem: React.FC<{
  message: FlashMessageType
  onClose: (id: string) => void
}> = ({message, onClose}) => {
  return (
    <motion.div
      layout
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={`${getBackgroundColor(message.type)} text-white p-4 rounded-lg shadow-lg flex items-center justify-between mb-2`}>
      <p className="mr-4">{message.message}</p>
      <button onClick={() => onClose(message.id)} className="text-white hover:text-gray-200 focus:outline-none" aria-label="Close message">
        <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </motion.div>
  )
}

export const FlashMessageContainer: React.FC = () => {
  const {messages, removeMessage} = useFlashMessage()

  return (
    <div className="fixed top-4 right-4 z-50 w-96 max-w-full">
      <AnimatePresence initial={false}>
        {messages.map((message) => (
          <FlashMessageItem key={message.id} message={message} onClose={removeMessage} />
        ))}
      </AnimatePresence>
    </div>
  )
}
